import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useMemo,
  useImperativeHandle,
} from "react"

import {
  isNumber,
  cleanValue,
  fixedDecimalValue,
  formatValue,
  getLocaleConfig,
  padTrimValue,
  getSuffix,
  repositionCursor
} from "./utils"

export const CurrencyInputWrapper = (props, ref) => {
  const {
    allowDecimals = true,
    allowNegativeValue = true,
    id,
    name,
    className,
    customInput,
    decimalsLimit,
    defaultValue,
    disabled = false,
    maxLength: userMaxLength,
    value: userValue,
    onValueChange,
    fixedDecimalLength,
    placeholder,
    decimalScale,
    prefix,
    suffix,
    intlConfig,
    step,
    min,
    max,
    disableGroupSeparators = false,
    disableAbbreviations = false,
    decimalSeparator: _decimalSeparator,
    groupSeparator: _groupSeparator,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    onKeyUp,
    transformRawValue,
    formatValueOnBlur = true,
    ...otherProps
  } = props

  if (_decimalSeparator && isNumber(_decimalSeparator)) {
    throw new Error("decimalSeparator cannot be a number")
  }

  if (_groupSeparator && isNumber(_groupSeparator)) {
    throw new Error("groupSeparator cannot be a number")
  }

  const localeConfig = useMemo(() => getLocaleConfig(intlConfig), [intlConfig])
  const decimalSeparator = _decimalSeparator || localeConfig.decimalSeparator || ""
  const groupSeparator = _groupSeparator || localeConfig.groupSeparator || ""

  if (
    decimalSeparator &&
    groupSeparator &&
    decimalSeparator === groupSeparator &&
    disableGroupSeparators === false
  ) {
    throw new Error("decimalSeparator cannot be the same as groupSeparator")
  }

  const formatValueOptions = {
    decimalSeparator,
    groupSeparator,
    disableGroupSeparators,
    intlConfig,
    prefix: prefix || localeConfig.prefix,
    suffix: suffix,
  }

  const cleanValueOptions = {
    decimalSeparator,
    groupSeparator,
    allowDecimals,
    decimalsLimit: decimalsLimit || fixedDecimalLength || 2,
    allowNegativeValue,
    disableAbbreviations,
    prefix: prefix || localeConfig.prefix,
    transformRawValue,
  }

  const [stateValue, setStateValue] = useState(() =>
    defaultValue != null
      ? formatValue({ ...formatValueOptions, decimalScale, value: String(defaultValue) })
      : userValue != null
        ? formatValue({ ...formatValueOptions, decimalScale, value: String(userValue) })
        : ""
  )
  const [dirty, setDirty] = useState(false)
  const [cursor, setCursor] = useState(0)
  const [changeCount, setChangeCount] = useState(0)
  const [lastKeyStroke, setLastKeyStroke] = useState(null)
  const inputRef = useRef(null)
  useImperativeHandle(ref, () => inputRef.current)

  /**
   * Process change in value
   */
  const processChange = (value, selectionStart) => {
    setDirty(true)

    const { modifiedValue, cursorPosition } = repositionCursor({
      selectionStart,
      value,
      lastKeyStroke,
      stateValue,
      groupSeparator,
    })

    const stringValue = cleanValue({ value: modifiedValue, ...cleanValueOptions })

    if (userMaxLength && stringValue.replace(/-/g, "").length > userMaxLength) {
      return
    }

    if (stringValue === "" || stringValue === "-" || stringValue === decimalSeparator) {
      onValueChange && onValueChange(undefined, name, { float: null, formatted: "", value: "" })
      setStateValue(stringValue)
      setCursor(1)
      return
    }

    const stringValueWithoutSeparator = decimalSeparator
      ? stringValue.replace(decimalSeparator, ".")
      : stringValue

    const numberValue = parseFloat(stringValueWithoutSeparator)

    const formattedValue =
      formatValue({
        value: stringValue,
        ...formatValueOptions,
      })

    if (cursorPosition != null) {
      let newCursor = cursorPosition + (formattedValue.length - value.length)
      newCursor = newCursor <= 0 ? (prefix ? prefix.length : 0) : newCursor

      setCursor(newCursor)
      setChangeCount(changeCount + 1)
    }

    setStateValue(formattedValue)

    if (onValueChange) {
      const values = {
        float: numberValue,
        formatted: formattedValue,
        value: stringValue,
      }
      onValueChange(stringValue, name, values)
    }
  }

  const toEnglishDigits = (num) => {
    const id = {
      "۰": "0",
      "۱": "1",
      "۲": "2",
      "۳": "3",
      "۴": "4",
      "۵": "5",
      "۶": "6",
      "۷": "7",
      "۸": "8",
      "۹": "9",
    }
    return num ? num.toString().replace(/[^0-9.]/g, (w) => id[w] || w) : ""
  }

  const handleOnChange = (event) => {
    const {
      target: { value, selectionStart }
    } = event

    const newValue =
      toEnglishDigits(value)

    processChange(newValue, selectionStart)

    onChange && onChange(event)
  }

  const handleOnFocus = (event) => {
    onFocus && onFocus(event)
    return stateValue ? stateValue.length : 0
  }

  const handleOnBlur = (event) => {
    const {
      target: { value },
    } = event

    const valueOnly = cleanValue({ value, ...cleanValueOptions })

    if (valueOnly === "-" || valueOnly === decimalSeparator || !valueOnly) {
      setStateValue("")
      onBlur && onBlur(event)
      return
    }

    const fixedDecimals = fixedDecimalValue(valueOnly, decimalSeparator, fixedDecimalLength)

    const newValue = padTrimValue(
      fixedDecimals,
      decimalSeparator,
      decimalScale !== undefined ? decimalScale : fixedDecimalLength
    )

    const numberValue = parseFloat(newValue.replace(decimalSeparator, "."))

    const formattedValue = formatValue({
      ...formatValueOptions,
      value: newValue,
    })

    if (onValueChange && formatValueOnBlur) {
      onValueChange(newValue, name, {
        float: numberValue,
        formatted: formattedValue,
        value: newValue,
      })
    }

    setStateValue(formattedValue)

    onBlur && onBlur(event)
  }

  const handleOnKeyDown = (event) => {
    const { key } = event

    setLastKeyStroke(key)

    if (step && (key === "ArrowUp" || key === "ArrowDown")) {
      event.preventDefault()
      setCursor(stateValue.length)

      const currentValue =
        parseFloat(
          userValue != null
            ? String(userValue).replace(decimalSeparator, ".")
            : cleanValue({ value: stateValue, ...cleanValueOptions })
        ) || 0
      const newValue = key === "ArrowUp" ? currentValue + step : currentValue - step

      if (min !== undefined && newValue < Number(min)) {
        return
      }

      if (max !== undefined && newValue > Number(max)) {
        return
      }

      const fixedLength = String(step).includes(".")
        ? Number(String(step).split(".")[1].length)
        : undefined

      processChange(
        String(fixedLength ? newValue.toFixed(fixedLength) : newValue).replace(
          ".",
          decimalSeparator
        )
      )
    }

    onKeyDown && onKeyDown(event)
  }

  const handleOnKeyUp = (event) => {
    const {
      key,
      currentTarget: { selectionStart },
    } = event
    if (key !== "ArrowUp" && key !== "ArrowDown" && stateValue !== "-") {
      const suffix = getSuffix(stateValue, { groupSeparator, decimalSeparator })

      if (suffix && selectionStart && selectionStart > stateValue.length - suffix.length) {
        /* istanbul ignore else */
        if (inputRef.current) {
          const newCursor = stateValue.length - suffix.length
          inputRef.current.setSelectionRange(newCursor, newCursor)
        }
      }
    }

    onKeyUp && onKeyUp(event)
  }

  useEffect(() => {
    if (userValue == null && defaultValue == null) {
      setStateValue("")
    }
  }, [defaultValue, userValue])

  useEffect(() => {
    if (
      dirty &&
      stateValue !== "-" &&
      inputRef.current &&
      document.activeElement === inputRef.current
    ) {
      inputRef.current.setSelectionRange(cursor, cursor)
    }
  }, [stateValue, cursor, inputRef, dirty, changeCount])

  const getRenderValue = () => {
    if (
      userValue != null &&
      stateValue !== "-" &&
      (!decimalSeparator || stateValue !== decimalSeparator)
    ) {
      return formatValue({
        ...formatValueOptions,
        decimalScale: dirty ? undefined : decimalScale,
        value: String(userValue),
      })
    }

    return stateValue
  }

  const inputProps = {
    type: "text",
    inputMode: "decimal",
    id,
    name,
    className,
    onChange: handleOnChange,
    onBlur: handleOnBlur,
    onFocus: handleOnFocus,
    onKeyDown: handleOnKeyDown,
    onKeyUp: handleOnKeyUp,
    placeholder,
    disabled,
    value: getRenderValue(),
    ref: inputRef,
    ...otherProps
  }

  if (customInput) {
    const CustomInput = customInput
    return <CustomInput {...inputProps} />
  }

  return <input {...inputProps} />
}


CurrencyInputWrapper.displayName = "CurrencyInputWrapper"

export default forwardRef(CurrencyInputWrapper)